<template>
	<div
		v-if="shouldShowDebug"
		:class="debugIconPositionClass"
		k-data-id="debugDrawer-toggle-container">
		<PhDetective
			class="w-6 h-6 text-theme-primary cursor-pointer hover:text-secondary"
			weight="light"
			k-data-id="debugDrawer-toggle-icon"
			@click="toggleDebug" />
	</div>

	<n-drawer
		v-model:show="isVisible"
		:width="drawerWidth"
		:height="drawerHeight"
		:placement="drawerPosition"
		:native-scrollbar="true"
		:mask-closable="false"
		:show-mask="false"
		:block-scroll="false"
		:resizable="true"
		:min-width="300"
		:max-width="800"
		k-data-id="debugDrawer-main"
		@update:width="handleWidthUpdate">
		<n-drawer-content title="Debug Information" :closable="true" k-data-id="debugDrawer-content">
			<div class="space-y-4">
				<div
					class="flex justify-between items-center mb-2"
					k-data-id="debugDrawer-controls-container">
					<div class="flex items-center gap-3">
						<div class="flex items-center gap-2" k-data-id="debugDrawer-position-controls">
							<PhAlignLeft
								:class="[
									'w-5 h-5 cursor-pointer transition-colors',
									drawerPosition === 'left'
										? 'text-theme-primary-500'
										: 'text-theme-muted hover:text-secondary'
								]"
								weight="light"
								k-data-id="debugDrawer-position-left"
								@click="drawerPosition = 'left'" />
							<PhAlignRight
								:class="[
									'w-5 h-5 cursor-pointer transition-colors',
									drawerPosition === 'right'
										? 'text-theme-primary-500'
										: 'text-theme-muted hover:text-secondary'
								]"
								weight="light"
								k-data-id="debugDrawer-position-right"
								@click="drawerPosition = 'right'" />
							<PhAlignBottom
								:class="[
									'w-5 h-5 cursor-pointer transition-colors',
									drawerPosition === 'bottom'
										? 'text-theme-primary-500'
										: 'text-theme-muted hover:text-secondary'
								]"
								weight="light"
								k-data-id="debugDrawer-position-bottom"
								@click="drawerPosition = 'bottom'" />
						</div>
						<span class="text-xs text-theme-muted" k-data-id="debugDrawer-timestamp">
							Updated: {{ lastRefreshTime }}
						</span>
					</div>
					<div class="flex items-center gap-2" k-data-id="debugDrawer-actions-container">
						<span
							class="text-xs text-theme-secondary"
							v-if="isVuexData"
							k-data-id="debugDrawer-vuex-indicator">
							<svg
								xmlns="http://www.w3.org/2000/svg"
								viewBox="1 1 2000 2000"
								width="16"
								height="16">
								<path
									fill="none"
									d="M1112 2001H1V1h2000v2000h-889m38.237-1447.99c-.77 1.18-1.664 2.297-2.291 3.549-47.945 95.745-95.869 191.501-143.798 287.255-1.01 2.02-2.064 4.017-3.31 6.438-1.173-2.489-2.015-4.373-2.938-6.218-47.63-95.214-95.26-190.428-142.941-285.616-1.003-2.002-2.587-3.713-3.915-6.327-.823-2.322-1.412-4.762-2.505-6.95-48.156-96.41-96.376-192.788-144.48-289.224-1.774-3.554-3.707-5.019-7.853-5.016-146.473.119-292.945.099-439.418.11-1.747 0-3.495.165-5.95.288 1.239 2.598 2.116 4.52 3.06 6.41C369.14 488.218 484.395 718.722 599.62 949.24c8.607 17.219 17.02 34.535 25.386 52.532-.2.446-.386.9-.604 1.337-118.775 237.509-237.554 475.015-356.322 712.526-5.763 11.525-11.43 23.098-17.494 35.364h5.519c146.984 0 293.967-.015 440.951.093 3.53.003 5.117-1.215 6.608-4.211a373750.621 373750.621 0 0 1 170.568-342.434c12.85-25.765 25.993-51.385 38.913-77.116 14.678-29.233 29.246-58.521 43.882-87.775 13.615-27.213 27.254-54.413 40.892-81.614.854-1.703 1.792-3.364 3.03-5.678 1.14 2.012 1.98 3.347 2.68 4.752 49.488 99.11 98.926 198.246 148.454 297.336 48.778 97.592 97.657 195.134 146.42 292.733 1.395 2.791 2.79 4.019 6.203 4.016 147.15-.118 294.3-.101 441.45-.114 1.556 0 3.112-.182 5.074-.305-1.013-2.157-1.734-3.78-2.527-5.365-115.395-230.771-230.8-461.537-346.183-692.314-8.637-17.276-17.112-34.633-25.502-52.67.844-1.806 1.646-3.63 2.537-5.412 122.949-245.925 245.904-491.847 368.85-737.773.863-1.724 1.606-3.508 2.807-6.149h-141.096c-101.302 0-202.605.028-303.907-.096-4.231-.005-6.393 1.218-8.319 5.08-48.363 97.034-96.88 193.99-145.316 290.988-.887 1.777-1.107 3.886-2.337 6.038z" />
								<path
									fill="#4FC08D"
									d="M1376.859 1001.051c8.55 17.32 17.024 34.677 25.661 51.953 115.383 230.777 230.788 461.543 346.183 692.314.793 1.586 1.514 3.208 2.527 5.365-1.962.123-3.518.305-5.073.305-147.15.013-294.301-.004-441.451.114-3.414.003-4.808-1.225-6.203-4.016-48.763-97.6-97.642-195.14-146.42-292.733-49.528-99.09-98.966-198.226-148.453-297.336-.702-1.405-1.541-2.74-2.681-4.752-1.238 2.314-2.176 3.975-3.03 5.678-13.638 27.2-27.277 54.401-40.892 81.614-14.636 29.254-29.204 58.542-43.882 87.775-12.92 25.731-26.062 51.35-38.913 77.116a373750.621 373750.621 0 0 0-170.568 342.434c-1.491 2.996-3.079 4.214-6.608 4.211-146.984-.108-293.967-.093-440.951-.093h-5.52c6.066-12.266 11.732-23.84 17.495-35.364 118.768-237.511 237.547-475.017 356.322-712.526.218-.437.403-.89.962-1.873 1.434-2.402 2.609-4.219 3.57-6.142 72.7-145.364 145.38-290.738 218.1-436.092 1.089-2.175 2.673-4.102 4.026-6.144 1.312 1.846 2.896 3.557 3.899 5.559 47.68 95.188 95.311 190.402 142.941 285.616.923 1.845 1.765 3.73 2.938 6.218 1.246-2.42 2.3-4.419 3.31-6.438 47.929-95.754 95.853-191.51 143.798-287.255.627-1.252 1.521-2.37 2.955-3.486 1.833 2.064 3.117 4.007 4.151 6.074 72.635 145.215 145.246 290.442 217.893 435.65 1.095 2.19 2.6 4.174 3.914 6.254z" />
								<path
									fill="#35495E"
									d="M1376.938 1000.692c-1.393-1.72-2.898-3.705-3.993-5.894-72.647-145.21-145.258-290.436-217.893-435.65-1.034-2.068-2.318-4.01-3.801-6.174.216-2.116.436-4.225 1.323-6.002 48.436-96.997 96.953-193.954 145.316-290.987 1.926-3.863 4.088-5.086 8.319-5.08 101.302.123 202.605.095 303.907.095h141.096c-1.201 2.641-1.944 4.425-2.806 6.15-122.947 245.925-245.902 491.847-368.85 737.772-.892 1.781-1.694 3.606-2.618 5.77zM851.052 552.475c-1.345 2.426-2.93 4.353-4.017 6.528-72.721 145.354-145.4 290.728-218.1 436.092-.962 1.923-2.137 3.74-3.502 5.78-8.793-17.099-17.206-34.415-25.813-51.634-115.226-230.518-230.48-461.023-345.721-691.533-.945-1.889-1.822-3.81-3.061-6.41 2.455-.122 4.203-.287 5.95-.287 146.473-.011 292.945.009 439.418-.11 4.146-.003 6.08 1.462 7.852 5.016 48.105 96.436 96.325 192.814 144.481 289.224 1.093 2.188 1.682 4.628 2.513 7.334z" />
							</svg>
						</span>
						<span class="text-xs text-theme-secondary" v-else>&nbsp;</span>
						<n-button
							size="tiny"
							@click="refreshDebugInfo"
							:loading="refreshingStructure"
							k-data-id="debugDrawer-action-refresh">
							<template #icon>
								<svg
									class="w-4 h-4"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
									xmlns="http://www.w3.org/2000/svg">
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
								</svg>
							</template>
							Refresh
						</n-button>
						<n-button
							size="tiny"
							@click="copyDebugInfo"
							:disabled="!hasDebugData"
							k-data-id="debugDrawer-action-copy">
							<template #icon>
								<svg
									class="w-4 h-4"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
									xmlns="http://www.w3.org/2000/svg">
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
								</svg>
							</template>
							Copy
						</n-button>
					</div>
				</div>

				<div v-if="hasDebugData" k-data-id="debugDrawer-data-container">
					<pre
						class="bg-gray-800 text-green-400 p-4 rounded text-xs overflow-auto max-h-[80vh]"
						k-data-id="debugDrawer-data-display"
						>{{ formattedDebugData }}</pre
					>
				</div>
				<div v-else class="text-theme-secondary italic" k-data-id="debugDrawer-no-data">
					No debug information available. Add debug info using:
					<pre
						class="bg-theme-secondary p-2 rounded text-sm mt-2"
						k-data-id="debugDrawer-usage-example">
store.dispatch('debug/addDebugInfo', {
  key: 'yourKey',
  value: yourValue
})</pre
					>
				</div>
			</div>
		</n-drawer-content>
	</n-drawer>
</template>

<script setup>
import { computed, ref, inject, onMounted, watch } from 'vue'
import { useStore } from 'vuex'
import { NDrawer, NDrawerContent, NButton } from 'naive-ui'
import { PhDetective, PhAlignLeft, PhAlignRight, PhAlignBottom } from '@phosphor-icons/vue'
import { DateTime } from 'luxon'
import { showSuccess } from '@utils/AlertService'

const user = inject('user')
const store = useStore()
const drawerWidth = ref(500)
const refreshingStructure = ref(false)
const lastRefreshTime = ref(null)
const currentDebugData = ref({})

const isVisible = computed({
	get: () => store.getters['debug/isDebugVisible'],
	set: () => store.dispatch('debug/toggleDebug')
})

const debugInfo = computed(() => store.getters['debug/getDebugInfo'])

const hasDebugData = computed(() => {
	return Object.keys(currentDebugData.value).length > 0
})

const formattedDebugData = computed(() => {
	return JSON.stringify(currentDebugData.value, null, 2)
})

const isVuexData = computed(() => {
	return Array.isArray(currentDebugData.value)
})

const shouldShowDebug = computed(() => {
	const isLocal = window.location.hostname === 'localhost'
	return isLocal || user?.value?.data?.role?.developer
})

const pageStructure = computed(() => {
	return store.getters['pageBuilder/getPageStructure']
})

const drawerPosition = ref('right')
const drawerHeight = ref(500)

const debugIconPositionClass = computed(() => {
	const baseClasses = 'fixed z-50'
	switch (drawerPosition.value) {
		case 'left':
			return `${baseClasses} top-4 left-8`
		case 'bottom':
			return `${baseClasses} bottom-4 right-8`
		default:
			return `${baseClasses} top-4 right-8`
	}
})

const toggleDebug = () => {
	store.dispatch('debug/toggleDebug')
}

const handleWidthUpdate = (width) => {
	if (drawerPosition.value !== 'bottom') {
		drawerWidth.value = width
	}
}

// Add watcher for position changes
watch(drawerPosition, (newPosition) => {
	if (newPosition === 'bottom') {
		drawerWidth.value = window.innerWidth
	} else {
		drawerWidth.value = 500
	}
})

const refreshDebugInfo = () => {
	refreshingStructure.value = true

	// Small delay to simulate refresh
	setTimeout(() => {
		updateDebugData()
		lastRefreshTime.value = DateTime.fromJSDate(new Date()).toLocaleString(
			DateTime.DATETIME_SHORT_WITH_SECONDS
		)
		refreshingStructure.value = false
	}, 300)
}

const updateDebugData = () => {
	// Check for page structure first
	const structure = pageStructure.value
	if (structure && Array.isArray(structure) && structure.length > 0) {
		currentDebugData.value = [...structure]
		return
	}

	// Otherwise use any available debug info
	if (Object.keys(debugInfo.value).length > 0) {
		currentDebugData.value = debugInfo.value
	} else {
		currentDebugData.value = {}
	}
}

const copyDebugInfo = () => {
	if (hasDebugData.value) {
		navigator.clipboard.writeText(formattedDebugData.value).then(() => {
			showSuccess('Debug info copied to clipboard')
		})
	}
}

// Initialize on mount
onMounted(() => {
	// Initial refresh of debug data
	refreshDebugInfo()

	// Add watcher for page structure changes
	store.watch(
		() => store.getters['pageBuilder/getPageStructure'],
		(newStructure) => {
			if (newStructure && Array.isArray(newStructure) && newStructure.length > 0) {
				currentDebugData.value = [...newStructure]
				lastRefreshTime.value = DateTime.fromJSDate(new Date()).toLocaleString(
					DateTime.DATETIME_SHORT_WITH_SECONDS
				)
			}
		},
		{ deep: true }
	)

	// Also watch for debug info changes
	store.watch(
		() => store.getters['debug/getDebugInfo'],
		(newInfo) => {
			// Skip if we already have structure data displayed (which is now an array)
			if (Array.isArray(currentDebugData.value) && currentDebugData.value.length > 0) {
				return
			}

			// Otherwise update with new debug info (which might be an object)
			if (Object.keys(newInfo).length > 0) {
				currentDebugData.value = newInfo
				lastRefreshTime.value = DateTime.fromJSDate(new Date()).toLocaleString(
					DateTime.DATETIME_SHORT_WITH_SECONDS
				)
			}
		},
		{ deep: true }
	)
})
</script>
